import { Link } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { AutomationCampaign } from "../../../../lib/marketingAutomation";
import type { CampaignStatus } from "../../../../lib/marketing";
import { StatusPill } from "../../../ui";
import { campaignTone } from "../status";
import { Panel, dt } from "../shared";

/**
 * Which campaigns the clock would pick up, and why it would pass over the rest.
 *
 * THE "WHY" IS THE WHOLE CARD. A scheduler that silently skips a campaign
 * somebody believes is running is this codebase's most repeated failure: the
 * thing looks alive, nothing is wrong, and nothing happens. Every row carries a
 * named reason, including the ones that are fine, so "the automation is not
 * doing anything" always has an answer on the screen that says it.
 *
 * The reasons are the server's own `AutoSkip` values rather than a second
 * opinion computed here, because a browser that decided eligibility for itself
 * would eventually disagree with the scheduler about a date.
 */
export function CampaignsCard({ campaigns }: { campaigns: AutomationCampaign[] }) {
  const t = useT();

  if (campaigns.length === 0) {
    return (
      <Panel title={t("marketing.auto.campaignsTitle")} description={t("marketing.auto.campaignsIntro")} icon={<ListChecks className="size-4" />}>
        <p className="crm-sub">{t("marketing.auto.campaignsEmpty")}</p>
      </Panel>
    );
  }

  return (
    <Panel
      title={t("marketing.auto.campaignsTitle")}
      description={t("marketing.auto.campaignsIntro")}
      icon={<ListChecks className="size-4" />}
    >
      <div className="overflow-x-auto">
        <table className="crm-table w-full">
          <thead>
            <tr>
              <th>{t("marketing.auto.colCampaign")}</th>
              <th>{t("marketing.auto.colStatus")}</th>
              <th>{t("marketing.auto.colLastRun")}</th>
              <th className="pl-4">{t("marketing.auto.colSkip")}</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/marketing/campaigns/${c.id}`} className="underline decoration-dotted underline-offset-2">
                    {c.name}
                  </Link>
                </td>
                <td>
                  {/* The campaign's own status tone, not a second colour
                      system invented for this page: a paused campaign has to
                      look the same here as it does in the list. */}
                  <StatusPill tone={campaignTone(c.status as CampaignStatus)}>
                    {t(`marketing.status.${c.status}`)}
                  </StatusPill>
                </td>
                <td className="crm-sub">{c.lastRunAt ? dt(c.lastRunAt) : t("marketing.auto.never")}</td>
                <td className="crm-sub pl-4">{t(`marketing.auto.skip.${c.skip}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
